import type { Bucket } from '../lib/progress.js'
import { useT } from '../lib/useT.js'

const CHART_HEIGHT = 144
/** Hauteur d'une barre vide : une place réservée, pas une absence. */
const EMPTY_HEIGHT = 0.3

/**
 * Barres en pilule : vert pour ce qui est fait, orange pour la période en
 * cours, hachures pour ce qui est vide. La barre sélectionnée affiche sa
 * valeur dans une bulle — les autres restent muettes.
 */
export function BarChart({
  buckets,
  selected,
  onSelect,
}: {
  buckets: readonly Bucket[]
  selected: string
  onSelect: (key: string) => void
}) {
  const t = useT()
  const max = Math.max(1, ...buckets.map((bucket) => bucket.minutes))
  const dense = buckets.length > 7

  return (
    <div className={`flex items-end ${dense ? 'gap-1' : 'gap-2'}`}>
      {buckets.map((bucket) => {
        const empty = bucket.minutes === 0
        const isSelected = bucket.key === selected
        const ratio = empty ? EMPTY_HEIGHT : Math.max(0.18, bucket.minutes / max)
        return (
          <button
            key={bucket.key}
            onClick={() => onSelect(bucket.key)}
            aria-pressed={isSelected}
            aria-label={`${bucket.fullLabel} : ${t('common.minutes', { count: bucket.minutes })}`}
            className="flex min-w-0 flex-1 flex-col items-center gap-2"
          >
            <span
              className="relative flex w-full items-end justify-center"
              style={{ height: CHART_HEIGHT }}
            >
              <span
                className={`w-full transition-[height] duration-300 ease-out ${
                  dense ? 'rounded-lg' : 'rounded-2xl'
                } ${
                  empty
                    ? 'hatched'
                    : bucket.isCurrent
                      ? 'bg-accent'
                      : 'bg-green'
                } ${isSelected ? '' : 'opacity-85'}`}
                style={{ height: `${ratio * (CHART_HEIGHT - 32)}px` }}
              />
              {isSelected && (
                <span
                  className="bg-night text-surface text-caption num absolute rounded-full px-2 py-1 font-bold whitespace-nowrap"
                  style={{ bottom: ratio * (CHART_HEIGHT - 32) + 6 }}
                >
                  {bucket.minutes}
                </span>
              )}
            </span>
            <span
              className={`text-caption h-4 whitespace-nowrap capitalize ${
                isSelected ? 'text-ink font-bold' : 'text-ink-3'
              }`}
            >
              {bucket.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}
